import { useMemo } from 'react';
import { IconLayer, PathLayer, ScatterplotLayer, TextLayer } from '@deck.gl/layers/typed';
import { AircraftAlert, TrailPoint, getAlertLevelColor } from '../features/adsb/adsbTypes';
import {
  getShapeIcon,
  getVerticalRateArrowIcon,
  getVerticalTrend,
  VerticalTrend,
} from '../features/adsb/adsbIcons';
import { destinationPoint } from '../geometry';
import { hexToRgb } from '../utils/color';

const KNOTS_TO_MS = 0.514444;
const MIN_TRACK_LENGTH_M = 500;
const TCAS_SYMBOL_SIZE_PX = 22;
const VERTICAL_RATE_ARROW_SIZE_PX = 13;

const getAlertColor = (alertLevel: string): [number, number, number] =>
  hexToRgb(getAlertLevelColor(alertLevel));

interface AdsbLayerData {
  aircraft: AircraftAlert;
  coordinates: [number, number];
  label: string;
}

interface VerticalRateLayerData {
  aircraft: AircraftAlert;
  coordinates: [number, number];
  trend: VerticalTrend;
}

interface TrackLineData {
  path: [number, number][];
  color: [number, number, number, number];
}

interface BreadcrumbData {
  coordinates: [number, number];
  color: [number, number, number, number];
}

/**
 * Hook that returns DeckGL layers for rendering ADS-B traffic
 * @param aircraftAlerts - Array of aircraft alerts to display
 * @param showTraffic - Whether to display traffic or not
 * @param trails - Historical position trails keyed by aircraft hex
 * @param now - Current timestamp for fading breadcrumbs
 * @param trailDurationMs - How long breadcrumb points remain visible
 * @param trackProjectionSeconds - How far ahead to project track lines based on speed
 * @returns Array of DeckGL layers
 */
const useAdsbAlerts = (
  aircraftAlerts: AircraftAlert[],
  showTraffic: boolean = true,
  trails: Record<string, TrailPoint[]> = {},
  now: number = Date.now(),
  trailDurationMs: number = 30000,
  trackProjectionSeconds: number = 60
) => {
  const layers = useMemo(() => {
    if (!showTraffic || aircraftAlerts.length === 0) {
      return [];
    }

    // Prepare data for layers
    const layerData: AdsbLayerData[] = aircraftAlerts.map((aircraft) => {
      const coordinates: [number, number] = [aircraft.lon, aircraft.lat];
      const label = `${aircraft.flight}\n${Math.round(aircraft.altitude)}m | ${Math.round(aircraft.groundSpeed * 1.852)}km/h`;

      return {
        aircraft,
        coordinates,
        label,
      };
    });

    const verticalRateData: VerticalRateLayerData[] = aircraftAlerts
      .map((aircraft) => {
        const trend = getVerticalTrend(aircraft);
        if (!trend) return null;
        return {
          aircraft,
          coordinates: [aircraft.lon, aircraft.lat] as [number, number],
          trend,
        };
      })
      .filter((d): d is VerticalRateLayerData => d !== null);

    const trackLineData: TrackLineData[] = aircraftAlerts.map((aircraft) => {
      const distanceMeters = Math.max(
        aircraft.groundSpeed * KNOTS_TO_MS * trackProjectionSeconds,
        MIN_TRACK_LENGTH_M
      );
      const endPoint = destinationPoint(
        aircraft.lat,
        aircraft.lon,
        aircraft.track,
        distanceMeters
      );
      const [r, g, b] = getAlertColor(aircraft.alertLevel);
      return {
        path: [[aircraft.lon, aircraft.lat], endPoint],
        color: [r, g, b, 200],
      };
    });

    const breadcrumbData: BreadcrumbData[] = [];
    Object.values(trails).forEach((points) => {
      if (points.length <= 1) return;

      // Exclude the most recent point — it's rendered as the main aircraft dot
      const historicalPoints = points.slice(0, -1);
      historicalPoints.forEach((point) => {
        const age = now - point.timestamp;
        if (age > trailDurationMs) return;

        const opacity = Math.max(0, Math.min(1, 1 - age / trailDurationMs));
        breadcrumbData.push({
          coordinates: [point.lon, point.lat],
          color: [150, 150, 150, Math.round(opacity * 255)],
        });
      });
    });

    return [
      // Breadcrumb trail (behind everything else)
      breadcrumbData.length > 0
        ? new ScatterplotLayer({
            id: 'adsb-breadcrumb-layer',
            data: breadcrumbData,
            getPosition: (d: BreadcrumbData) => d.coordinates,
            getFillColor: (d: BreadcrumbData) => d.color,
            getRadius: 3,
            radiusUnits: 'pixels',
            stroked: false,
            pickable: false,
          })
        : null,

      // Track projection lines
      new PathLayer({
        id: 'adsb-track-layer',
        data: trackLineData,
        getPath: (d: TrackLineData) => d.path,
        getColor: (d: TrackLineData) => d.color,
        getWidth: 2,
        widthUnits: 'pixels',
        lineWidthMinPixels: 1,
        pickable: false,
      }),

      // TCAS-style traffic symbols (diamond / circle / square)
      new IconLayer({
        id: 'adsb-position-layer',
        data: layerData,
        getPosition: (d: AdsbLayerData) => d.coordinates,
        getIcon: (d: AdsbLayerData) => getShapeIcon(d.aircraft.alertLevel),
        getColor: (d: AdsbLayerData) => {
          const [r, g, b] = getAlertColor(d.aircraft.alertLevel);
          return [r, g, b, 255];
        },
        getSize: TCAS_SYMBOL_SIZE_PX,
        sizeUnits: 'pixels',
        pickable: false,
      }),

      // Vertical-rate trend arrows (climb/descent >= 500 fpm)
      verticalRateData.length > 0
        ? new IconLayer({
            id: 'adsb-vertical-rate-layer',
            data: verticalRateData,
            getPosition: (d: VerticalRateLayerData) => d.coordinates,
            getIcon: (d: VerticalRateLayerData) => getVerticalRateArrowIcon(d.trend),
            getColor: (d: VerticalRateLayerData) => {
              const [r, g, b] = getAlertColor(d.aircraft.alertLevel);
              return [r, g, b, 255];
            },
            getSize: VERTICAL_RATE_ARROW_SIZE_PX,
            sizeUnits: 'pixels',
            getPixelOffset: [16, 0],
            pickable: false,
          })
        : null,

      // Aircraft labels
      new TextLayer({
        id: 'adsb-label-layer',
        data: layerData,
        getPosition: (d: AdsbLayerData) => d.coordinates,
        getText: (d: AdsbLayerData) => d.label,
        getColor: (d: AdsbLayerData) => {
          const [r, g, b] = getAlertColor(d.aircraft.alertLevel);
          return [r, g, b, 255];
        },
        getSize: 12,
        getAngle: 0,
        getTextAnchor: 'middle',
        getAlignmentBaseline: 'top',
        getPixelOffset: [0, 13],
        fontFamily: 'Arial, sans-serif',
        fontWeight: 'bold',
        outlineWidth: 2,
        outlineColor: [0, 0, 0, 255],
        pickable: false,
      }),
    ].filter(Boolean);
  }, [aircraftAlerts, showTraffic, trails, now, trailDurationMs, trackProjectionSeconds]);

  return layers;
};

export default useAdsbAlerts;
